// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useTransactions } from './use-transactions';
import { RefreshControl } from '../refresh-control';
import { Loader } from '../loader';
import {
  IconButton,
  PeriodPicker,
  Portal,
  SearchBar,
  SectionContainer,
} from '@shared/ui-kit';
import { Tooltip } from '@mui/material';
import { TransactionsTable } from './transactions-table';
import { TransactionDrawer } from './transaction-drawer';
import { AccountsSelector } from '../accounts-selector';

export const Transactions = defineComponent({
  displayName: 'Transactions',
  useSetup: useTransactions,
  View: ({
    onRefresh,
    status,
    hasData,
    t,
    textSearch,
    handleTextSearch,
    transactions,
    onDownloadTransactions,
    updatedDate,
    refreshMode,
    institutionId,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    periodPicker,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
    selectedTransaction,
    drawerOpen,
    openTransactionDrawer,
    onDrawerClose,
    onDrawerOpen,
  }) => (
    <>
      <TransactionDrawer
        transaction={selectedTransaction}
        open={drawerOpen}
        onClose={onDrawerClose}
        onOpen={onDrawerOpen}
      />

      <Portal container="aggregation-institutions-actions">
        <AccountsSelector
          data={accountOptions}
          onChange={onChangeAccounts}
          selectedAccounts={selectedAccounts}
          tags={tags}
          selectedTag={selectedTag}
          onTagSelect={setSelectedTag}
          tagsByObject={tagsByObject}
        />
      </Portal>

      <div className="flex flex-col gap-y-6">
        <div className="flex w-full flex-col items-stretch gap-y-3 md:flex-row md:items-center md:justify-between">
          <RefreshControl
            updatedDate={updatedDate}
            onRefresh={onRefresh}
            disabled={status === 'pending'}
            mode={refreshMode}
          />

          <div className="flex shrink-0 justify-end">
            <PeriodPicker
              onValueChange={periodPicker.onPeriodChange}
              defaultValue={periodPicker.defaultHist}
              availableOptions={['1M', '3M', '6M', '1Y']}
            />
          </div>
        </div>

        <Loader
          status={status}
          shouldDisplayEmptyState={!hasData}
          institution={institutionId}
          onRefresh={onRefresh}
        >
          {hasData && (
            <SectionContainer
              headerClass="mb-4 flex flex-col md:flex-row gap-4"
              title={t('aggregator.transactions', { ns: 'aggregator' })}
              actions={
                <div className="flex items-center gap-6">
                  <SearchBar
                    placeholder={t('common.search', { ns: 'common' })}
                    value={textSearch}
                    onChangeValue={handleTextSearch}
                    dataTestId="transactions-search-bar"
                  />
                  <Tooltip
                    title={t('aggregator.download-transactions', {
                      ns: 'aggregator',
                    })}
                  >
                    <IconButton
                      icon="Download"
                      onClick={onDownloadTransactions}
                      data-testid="download-transactions"
                      disabled={transactions.length === 0}
                    />
                  </Tooltip>
                </div>
              }
            >
              <TransactionsTable
                data={transactions}
                onRowClick={openTransactionDrawer}
              />
            </SectionContainer>
          )}
        </Loader>
      </div>
    </>
  ),
});
